import React from "react";
import { AlertTriangle, ArrowLeft, RefreshCw } from "lucide-react";
import { LLink } from "@/i18n/LLink";

const MESSAGES = {
  nl: {
    eyebrow: "Er ging iets mis",
    title: "Deze pagina kon niet worden geladen",
    body:
      "Sorry, er trad een onverwachte fout op. Onze excuses. Je kunt de pagina proberen te herladen of teruggaan naar de homepage — de rest van Lemmer Leeft doet het gewoon.",
    reload: "Pagina opnieuw laden",
    home: "Terug naar de homepage",
  },
  de: {
    eyebrow: "Etwas ist schiefgelaufen",
    title: "Diese Seite konnte nicht geladen werden",
    body:
      "Entschuldigung, es ist ein unerwarteter Fehler aufgetreten. Sie können die Seite neu laden oder zur Startseite zurückkehren — der Rest von Lemmer Leeft funktioniert normal.",
    reload: "Seite neu laden",
    home: "Zurück zur Startseite",
  },
  en: {
    eyebrow: "Something went wrong",
    title: "This page could not be loaded",
    body:
      "Sorry, an unexpected error occurred. You can try to reload the page or head back to the home page — the rest of Lemmer Leeft still works fine.",
    reload: "Reload page",
    home: "Back to the home page",
  },
};

const detectLang = () => {
  if (typeof window === "undefined") return "nl";
  const p = window.location.pathname || "";
  if (p.startsWith("/de")) return "de";
  if (p.startsWith("/en")) return "en";
  return "nl";
};

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  handleReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const lang = detectLang();
    const t = MESSAGES[lang] || MESSAGES.nl;

    return (
      <div data-testid="error-boundary-fallback" className="bg-white">
        <section className="ll-section-soft py-16 sm:py-24">
          <div className="ll-container max-w-2xl text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white shadow-md mb-6">
              <AlertTriangle className="w-7 h-7 text-[#2E9CD6]" />
            </div>
            <span className="inline-block text-[#2E9CD6] font-[Poppins] font-semibold text-xs uppercase tracking-[0.2em] mb-3">
              {t.eyebrow}
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-[#173A5E]">
              {t.title}
            </h1>
            <p className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed mb-8">
              {t.body}
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button
                type="button"
                onClick={this.handleReload}
                data-testid="error-boundary-reload"
                className="ll-btn-primary"
              >
                <RefreshCw className="w-4 h-4" />
                {t.reload}
              </button>
              <LLink
                to="/"
                data-testid="error-boundary-home"
                className="ll-btn-outline"
              >
                <ArrowLeft className="w-4 h-4" />
                {t.home}
              </LLink>
            </div>
          </div>
        </section>
      </div>
    );
  }
}

export default ErrorBoundary;
